import { combineLatest as observableCombineLatest, empty as observableEmpty, from as observableFrom, of as observableOf, zip as observableZip, Subject, } from "rxjs";
import { bufferCount, catchError, distinctUntilChanged, filter, finalize, first, map, mergeMap, publish, publishReplay, refCount, retry, scan, startWith, switchMap, timeout, withLatestFrom, } from "rxjs/operators";
import { GraphCalculator } from "../graph/GraphCalculator";
import { GraphMode } from "../graph/GraphMode";
import { EdgeDirection } from "../graph/edge/EdgeDirection";
import { State } from "../state/State";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
export class PlayService {
    constructor(graphService, stateService, graphCalculator) {
        this._subscriptions = new SubscriptionHolder();
        this._graphService = graphService;
        this._stateService = stateService;
        this._graphCalculator = graphCalculator ? graphCalculator : new GraphCalculator();
        const subs = this._subscriptions;
        this._directionSubject$ = new Subject();
        this._direction$ = this._directionSubject$.pipe(startWith(EdgeDirection.Next), publishReplay(1), refCount());
        subs.push(this._direction$.subscribe());
        this._playing = false;
        this._playingSubject$ = new Subject();
        this._playing$ = this._playingSubject$.pipe(startWith(this._playing), publishReplay(1), refCount());
        subs.push(this._playing$.subscribe());
        this._speed = 0.62;
        this._speedSubject$ = new Subject();
        this._speed$ = this._speedSubject$.pipe(startWith(this._speed), publishReplay(1), refCount());
        subs.push(this._speed$.subscribe());
        this._timelinetoggle = false;
        this._timelinetoggleSubject$ = new Subject();
        this._timelinetoggle$ = this._timelinetoggleSubject$.pipe(startWith(this._timelinetoggle), publishReplay(1), refCount());
        subs.push(this._timelinetoggle$.subscribe());
        this._nodesAhead = this._mapNodesAhead(this._mapSpeed(this._speed));
        this._bridging$ = null;
    }
    get playing() {
        return this._playing;
    }
    get direction$() {
        return this._direction$;
    }
    get playing$() {
        return this._playing$;
    }
    get speed$() {
        return this._speed$;
    }
    get timelinetoggle$() {
        return this._timelinetoggle$;
    }
    get bridge$() {
        return this._stateService.currentNode$.pipe(switchMap((node) => {
            return node.sequenceEdges$.pipe(switchMap((edgeStatus) => {
                let nextKey = null;
                let prevKey = null;
                for (const edge of edgeStatus.edges) {
                    if (edge.data.direction === EdgeDirection.Next) {
                        nextKey = edge.to;
                    }
                    if (edge.data.direction === EdgeDirection.Prev) {
                        prevKey = edge.to;
                    }
                }
                const increasingTime = !nextKey ? true : false;
                if (!nextKey || !prevKey) {
                    return this._bridge$(node, increasingTime);
                }
                return observableOf(null);
            }));
        }));
    }
    play() {
        if (this._playing) {
            return;
        }
        this._stateService.cutNodes();
        const stateSpeed = this._setSpeed(this._speed);
        this._stateService.setSpeed(stateSpeed);
        this._graphModeSubscription = this._speed$
            .pipe(map((speed) => {
            return speed > PlayService.sequenceSpeed ? GraphMode.Sequence : GraphMode.Spatial;
        }), distinctUntilChanged())
            .subscribe((mode) => {
            this._graphService.setGraphMode(mode);
        });
        this._cacheSubscription = observableCombineLatest(
        // NOTE: Important.
        this._stateService.currentNode$.pipe(map((node) => {
            return [node.sequenceKey, node.key];
        }), distinctUntilChanged(undefined, ([sequenceKey, nodeKey]) => {
            return sequenceKey;
        })), this._graphService.graphMode$, this._direction$)
            .pipe(switchMap(([[sequenceKey, nodeKey], mode, direction]) => {
            // NOTE: (more)
            //debugger;
            if (direction !== EdgeDirection.Next && direction !== EdgeDirection.Prev) {
                return observableOf([undefined, direction]);
            }
            /* console.log("graphmode.seq ", mode === GraphMode.Sequence); */
            const sequence$ = (mode === GraphMode.Sequence
                ? this._graphService.cacheSequenceNodes$(sequenceKey, nodeKey)
                : this._graphService.cacheSequence$(sequenceKey)).pipe(retry(3), catchError((error) => {
                console.error(error);
                return observableOf(undefined);
            }));
            return observableCombineLatest(sequence$, observableOf(direction));
        }), switchMap(([sequence, direction]) => {
            // NOTE: (more)
            //debugger;
            if (sequence === undefined) {
                return observableEmpty();
            }
            const sequenceKeys = sequence.keys.slice();
            if (direction === EdgeDirection.Prev) {
                sequenceKeys.reverse();
            }
            return this._stateService.currentState$.pipe(map((frame) => {
                //debugger;
                return [
                    frame.state.trajectory[frame.state.trajectory.length - 1].key,
                    frame.state.nodesAhead,
                ];
            }), scan(([lastRequestKey, previousRequestKeys], [lastTrajectoryKey, nodesAhead]) => {
                if (lastRequestKey === undefined) {
                    lastRequestKey = lastTrajectoryKey;
                }
                const lastIndex = sequenceKeys.length - 1;
                if (nodesAhead >= this._nodesAhead || sequenceKeys[lastIndex] === lastRequestKey) {
                    return [lastRequestKey, []];
                }
                const current = sequenceKeys.indexOf(lastTrajectoryKey);
                const start = sequenceKeys.indexOf(lastRequestKey) + 1;
                const end = Math.min(lastIndex, current + this._nodesAhead - nodesAhead) + 1;
                if (end <= start) {
                    return [lastRequestKey, []];
                }
                return [sequenceKeys[end - 1], sequenceKeys.slice(start, end)];
            }, [undefined, []]), mergeMap(([lastRequestKey, newRequestKeys]) => {
                return observableFrom(newRequestKeys);
            }));
        }), mergeMap((key) => {
            return this._graphService.cacheNode$(key).pipe(catchError(() => {
                return observableEmpty();
            }));
        }, 6))
            .subscribe();
        this._playingSubscription = this._stateService.currentState$
            .pipe(filter((frame) => {
            return frame.state.nodesAhead < this._nodesAhead;
        }), distinctUntilChanged(undefined, (frame) => {
            return frame.state.lastNode.key;
        }), map((frame) => {
            const lastNode = frame.state.lastNode;
            const trajectory = frame.state.trajectory;
            let increasingTime = undefined;
            for (let i = trajectory.length - 2; i >= 0; i--) {
                const node = trajectory[i];
                if (node.sequenceKey !== lastNode.sequenceKey) {
                    break;
                }
                if (node.capturedAt !== lastNode.capturedAt) {
                    increasingTime = node.capturedAt < lastNode.capturedAt;
                    break;
                }
            }
            return [frame.state.lastNode, increasingTime];
        }), withLatestFrom(this._direction$), switchMap(([[node, increasingTime], direction]) => {
            return observableZip(([EdgeDirection.Next, EdgeDirection.Prev].indexOf(direction) > -1
                ? node.sequenceEdges$
                : node.spatialEdges$).pipe(first((status) => {
                return status.cached;
            }), timeout(15000)), observableOf(direction)).pipe(map(([s, d]) => {
                for (const edge of s.edges) {
                    if (edge.data.direction === d) {
                        return edge.to;
                    }
                }
                if (!increasingTime) {
                    increasingTime = d == EdgeDirection.Next ? true : false;
                }
                return null;
            }), switchMap((key) => {
                return key != null
                    ? this._graphService.cacheNode$(key)
                    : this._bridge$(node, increasingTime).pipe(filter((n) => {
                        return !!n;
                    }));
            }));
        }))
            .subscribe((node) => {
            this._stateService.appendNodes([node]);
        }, (error) => {
            console.error(error);
            this.stop();
        });
        this._clearSubscription = this._stateService.currentNode$
            .pipe(bufferCount(1, 10))
            .subscribe((nodes) => {
            this._stateService.clearPriorNodes();
        });
        this._setPlaying(true);
        const currentLastNodes$ = this._stateService.currentState$.pipe(map((frame) => {
            return frame.state;
        }), distinctUntilChanged(([kc1, kl1], [kc2, kl2]) => {
            return kc1 === kc2 && kl1 === kl2;
        }, (state) => {
            return [state.currentNode.key, state.lastNode.key];
        }), filter((state) => {
            return (state.currentNode.key === state.lastNode.key && state.currentIndex === state.trajectory.length - 1);
        }), map((state) => {
            return state.currentNode;
        }));
        this._stopSubscription = observableCombineLatest(currentLastNodes$, this._direction$)
            .pipe(switchMap(([node, direction]) => {
            // debugger;
            const edgeStatus$ = ([EdgeDirection.Next, EdgeDirection.Prev].indexOf(direction) > -1
                ? node.sequenceEdges$
                : node.spatialEdges$).pipe(first((status) => {
                return status.cached;
            }), timeout(15000), catchError((error) => {
                console.error(error);
                return observableOf({
                    cached: false,
                    edges: [],
                });
            }));
            return observableCombineLatest(observableOf(direction), edgeStatus$).pipe(map(([d, es]) => {
                for (const edge of es.edges) {
                    if (edge.data.direction === d) {
                        return true;
                    }
                }
                return false;
            }));
        }), mergeMap((hasEdge) => {
            if (hasEdge || !this._bridging$) {
                return observableOf(hasEdge);
            }
            return this._bridging$.pipe(map((node) => {
                return node != null;
            }), catchError((error) => {
                console.error(error);
                return observableOf(false);
            }));
        }), first((hasEdge) => {
            return !hasEdge;
        }))
            .subscribe(undefined, undefined, () => {
            this.stop();
        });
        if (this._stopSubscription.closed) {
            this._stopSubscription = null;
        }
        this._earthSubscription = this._stateService.state$
            .pipe(map((state) => {
            return state === State.Earth;
        }), distinctUntilChanged(), first((earth) => {
            return earth;
        }))
            .subscribe(undefined, undefined, () => {
            this.stop();
        });
        if (this._earthSubscription.closed) {
            this._earthSubscription = null;
        }
    }
    dispose() {
        this.stop();
        this._subscriptions.unsubscribe();
    }
    setDirection(direction) {
        this._direction = direction;
        this._directionSubject$.next(direction);
    }
    get direction() {
        return this._direction;
    }
    setSpeed(speed) {
        speed = Math.max(0, Math.min(1, speed));
        if (speed === this._speed) {
            return;
        }
        const stateSpeed = this._setSpeed(speed);
        if (this._playing) {
            this._stateService.setSpeed(stateSpeed);
        }
        this._speedSubject$.next(this._speed);
    }
    stop() {
        if (!this._playing) {
            return;
        }
        if (this._stopSubscription) {
            if (!this._stopSubscription.closed) {
                this._stopSubscription.unsubscribe();
            }
            this._stopSubscription = null;
        }
        if (this._earthSubscription) {
            if (!this._earthSubscription.closed) {
                this._earthSubscription.unsubscribe();
            }
            this._earthSubscription = null;
        }
        this._graphModeSubscription.unsubscribe();
        this._graphModeSubscription = null;
        this._cacheSubscription.unsubscribe();
        this._cacheSubscription = null;
        this._playingSubscription.unsubscribe();
        this._playingSubscription = null;
        this._clearSubscription.unsubscribe();
        this._clearSubscription = null;
        this._stateService.setSpeed(1);
        this._stateService.cutNodes();
        this._graphService.setGraphMode(GraphMode.Spatial);
        this._setPlaying(false);
    }
    _bridge$(node, increasingTime) {
        if (increasingTime === undefined) {
            return observableOf(null);
        }
        const boundingBox = this._graphCalculator.boundingBoxCorners(node.latLon, 25);
        this._bridging$ = this._graphService.cacheBoundingBox$(boundingBox[0], boundingBox[1]).pipe(mergeMap((nodes) => {
            let nextNode = null;
            for (const n of nodes) {
                if (n.sequenceKey === node.sequenceKey ||
                    !n.cameraUuid ||
                    n.cameraUuid !== node.cameraUuid ||
                    n.capturedAt === node.capturedAt ||
                    n.capturedAt > node.capturedAt !== increasingTime) {
                    continue;
                }
                const delta = Math.abs(n.capturedAt - node.capturedAt);
                if (delta > 15000) {
                    continue;
                }
                if (!nextNode || delta < Math.abs(nextNode.capturedAt - node.capturedAt)) {
                    nextNode = n;
                }
            }
            return nextNode ? this._graphService.cacheNode$(nextNode.key) : observableOf(null);
        }), finalize(() => {
            this._bridging$ = null;
        }), publish(), refCount());
        return this._bridging$;
    }
    _mapSpeed(speed) {
        const x = 3.0 * speed - 1;
        return Math.pow(10, x) - 0.2 * x;
    }
    _mapNodesAhead(stateSpeed) {
        return Math.round(Math.max(10, Math.min(50, 8 + 6 * stateSpeed)));
    }
    _setPlaying(playing) {
        this._playing = playing;
        this._playingSubject$.next(playing);
    }
    _setSpeed(speed) {
        this._speed = speed;
        const stateSpeed = this._mapSpeed(this._speed);
        this._nodesAhead = this._mapNodesAhead(stateSpeed);
        return stateSpeed;
    }
    setTimelinetoggle(timelinetoggle) {
        this._timelinetoggle = timelinetoggle;
        this._timelinetoggleSubject$.next(timelinetoggle);
    }
}
PlayService.sequenceSpeed = -1;
//# sourceMappingURL=PlayService.js.map